import { useLayoutEffect, useRef, useState } from 'react';
import { LazyMotion, m } from 'framer-motion';
import { afterFirstPaint } from '@/api/base44Lazy';

// Slim framer-motion setup: `m` + LazyMotion instead of the full `motion` component.
// The animation engine (domAnimation) is fetched as a separate small file — and only once the
// page's own content is on screen, so it never competes with it. (Below-the-fold content is
// out of view anyway, so it's fine for the fade-in to be ready a moment later.)
const loadFeatures = () =>
  new Promise((resolve) => afterFirstPaint(resolve)).then(() =>
    import('@/lib/motionFeatures').then((mod) => mod.default)
  );

// Speed rule: anything already on screen when the page opens must appear INSTANTLY (Google measures
// it as the page's main content). So we first render a plain block, measure it before the first
// paint, and only switch on the fade-in if it sits below the visible screen.
export default function ScrollReveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect && rect.top > window.innerHeight) setAnimate(true);
  }, []);

  if (!animate) {
    return <div ref={ref} className={className}>{children}</div>;
  }

  return (
    <LazyMotion features={loadFeatures} strict>
      <m.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        className={className}
      >
        {children}
      </m.div>
    </LazyMotion>
  );
}
